import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { DialogBox, DialogBoxFooterType, NoLink, SelectInput, ZeroToInfiniteGrid } from "~/core";
import TableCell from "~/core/components/tables/data-table/components/table-cell";
import { withEditableGrid } from "~/hocs";
import { mapToPicklistValue, prepareSelectableOptions } from "~/admin/utils";
import { GUID, ID, NAME, PICKLIST_GUID, VALUE } from "~/admin/data";
import { IS_SELECTABLE } from "~/admin/setup/customer/use-product-list";
import { messages } from "./../i18n-messages";
import {
    createAddLinkLabelText,
    createAddEditModalTitle,
    messages as globalMessages,
} from "~/i18n-messages";

// Styles
import "./component.css";

const PROPS_CROP_NAME = "cropName";
const PROPS_CROP_GUID = "cropGuid";

class CropName extends Component {
    static propTypes = {
        deleteItem: PropTypes.func,
        editData: PropTypes.object,
        formatMessage: PropTypes.func,
        isDeleting: PropTypes.bool,
        isEditing: PropTypes.bool,
        onAdd: PropTypes.func,
        onAddOrEditItem: PropTypes.func,
        onChange: PropTypes.func,
        onDelete: PropTypes.func,
        onEdit: PropTypes.func,
        options: PropTypes.object,
        record: PropTypes.array,
        renderDeleteModal: PropTypes.func,
        toggleModal: PropTypes.func,
        unlinkedCropGuids: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            editOptions: [],
            editingItem: false,
            records: [],
            lastRecord: null,
            lastCropNameOptions: null,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { record, options = {} } = nextProps;
        const cropNameOptions = options[PROPS_CROP_NAME];
        if (record === prevState.lastRecord && cropNameOptions === prevState.lastCropNameOptions) {
            return null;
        }
        const records = (record || []).map((recordItem) => {
            const selectedCropName = (cropNameOptions || []).find((cropNameItem) => {
                return cropNameItem.guid === recordItem[PROPS_CROP_GUID];
            });
            return {
                ...recordItem,
                [PROPS_CROP_NAME]:
                    (selectedCropName && selectedCropName[NAME]) || recordItem[PROPS_CROP_NAME],
            };
        });
        return {
            records,
            lastRecord: record,
            lastCropNameOptions: cropNameOptions,
        };
    }

    initializeDropdowns = (options = {}) => {
        this.setState({
            editOptions: prepareSelectableOptions(options[PROPS_CROP_NAME], {
                guid: GUID,
                label: NAME,
                id: ID,
            }),
        });
    };

    onEdit = (record, index) => {
        this.initializeDropdowns(this.props.options);
        this.setState({ editingItem: true });
        const keysToUpdate = [
            {
                name: [PROPS_CROP_GUID],
                key: PICKLIST_GUID,
            },
            {
                name: [PROPS_CROP_NAME],
                key: VALUE,
            },
        ];
        this.props.onEdit({ record, index, keysToUpdate });
    };

    onDelete = (record, index) => {
        this.props.onDelete({ record, index });
    };

    onAdd = () => {
        this.initializeDropdowns(this.props.options);
        this.setState({ editingItem: false });
        this.props.onAdd();
    };

    renderAddEditModal = () => {
        const {
            formatMessage,
            isEditing,
            editData,
            onAddOrEditItem,
            toggleModal,
            unlinkedCropGuids,
        } = this.props;
        const isSelectedCropUnlinked = Boolean(
            unlinkedCropGuids && unlinkedCropGuids.has(editData[PROPS_CROP_GUID])
        );
        return (
            <DialogBox
                action="save"
                actionDisabled={!editData[PROPS_CROP_GUID]}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                forceOverflow
                isOpen={isEditing}
                onAction={() => onAddOrEditItem()}
                onClose={() => toggleModal("isEditing", false)}
                title={createAddEditModalTitle(
                    this.state.editingItem,
                    formatMessage,
                    messages.crop
                )}
                testId="crop-name-add-edit-modal"
            >
                <SelectInput
                    tabIndex={0}
                    autoFocus
                    optionIsHiddenKey={IS_SELECTABLE}
                    openOnFocus={false}
                    options={this.state.editOptions}
                    placeholderText={formatMessage(messages.cropName)}
                    value={mapToPicklistValue({
                        options: this.state.editOptions,
                        selectedGuid: editData[PROPS_CROP_GUID],
                    })}
                    onChange={(value) => {
                        this.props.onChange({
                            [PROPS_CROP_GUID]: value ? value.guid : "",
                        });
                    }}
                    containerClassNames={[{ "select-form-input-error": isSelectedCropUnlinked }]}
                    toolTip={
                        isSelectedCropUnlinked
                            ? formatMessage(globalMessages.cropNotLinkedInAgvance)
                            : undefined
                    }
                    testId="crop-name-crop-name-select"
                />
            </DialogBox>
        );
    };

    renderCropNameCell = (value, record) => {
        const { formatMessage, unlinkedCropGuids } = this.props;
        const isUnlinked = Boolean(
            unlinkedCropGuids && unlinkedCropGuids.has(record[PROPS_CROP_GUID])
        );
        const title = isUnlinked
            ? `${value} \n ${formatMessage(globalMessages.cropNotLinkedInAgvance)}`
            : `${value}`;
        return (
            <TableCell
                key={`row-${PROPS_CROP_NAME}`}
                className={classnames({ "unlinked-crop": isUnlinked })}
                title={title}
            >
                <p>{value}</p>
            </TableCell>
        );
    };

    render() {
        const { formatMessage, renderDeleteModal } = this.props;
        const { records } = this.state;
        return (
            <div className={"form-section-child-stretch mini-grid"}>
                {records && records.length > 0 && (
                    <ZeroToInfiniteGrid
                        records={records}
                        columns={{
                            [PROPS_CROP_NAME]: {
                                title: formatMessage(messages.name),
                                printer: this.props.unlinkedCropGuids
                                    ? this.renderCropNameCell
                                    : undefined,
                            },
                        }}
                        className={"cell-stretch"}
                        onEdit={this.onEdit}
                        onDelete={this.onDelete}
                        showHeader={false}
                    />
                )}
                {this.renderAddEditModal()}
                {renderDeleteModal()}
                <div className="sub-description-container">
                    <NoLink
                        className="add-link"
                        label={createAddLinkLabelText(formatMessage, messages.crop)}
                        onClick={this.onAdd}
                    ></NoLink>
                    <span className="sub-description">
                        {formatMessage(messages.cropSubDescription)}
                    </span>
                </div>
            </div>
        );
    }
}

export default withEditableGrid(CropName);
